import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * TagSelectField - compact multi-select with integrated search input
 *
 * Designed for autocomplete-sourced fields (e.g. geographies). The search input
 * sits inline with the selected chips; selecting an option adds it as a chip.
 * Overflow beyond maxVisible chips is collapsed into a "+N" tooltip trigger.
 * The dropdown opens on focus and closes on outside pointerdown.
 *
 * Usage:
 * ```tsx
 * <TagSelectField
 *   label="Geographies of Operation"
 *   options={geoOptions}
 *   selectedValues={selected}
 *   onSelectionChange={setSelected}
 * />
 * ```
 */
import * as React from 'react';
import { RequiredLabel } from './RequiredLabel.js';
export function TagSelectField({ label, required = false, showRequiredText = false, error, options, selectedValues, onSelectionChange, helperText, placeholder = 'Search\u2026', id, name, }) {
    const inputId = id || React.useId();
    const [dropdownOpen, setDropdownOpen] = React.useState(false);
    const [query, setQuery] = React.useState('');
    const containerRef = React.useRef(null);
    const inputRef = React.useRef(null);
    React.useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setDropdownOpen(false);
                setQuery('');
            }
        };
        document.addEventListener('pointerdown', handler, true);
        return () => document.removeEventListener('pointerdown', handler, true);
    }, []);
    const selectedSet = React.useMemo(() => new Set(selectedValues), [selectedValues]);
    const filtered = React.useMemo(() => {
        const q = query.toLowerCase().trim();
        return options.filter((o) => !selectedSet.has(o.value) && (!q || o.label.toLowerCase().includes(q)));
    }, [options, selectedSet, query]);
    const handleRemove = (value) => {
        onSelectionChange(selectedValues.filter((v) => v !== value));
    };
    const handleAdd = (value) => {
        onSelectionChange([...selectedValues, value]);
        setQuery('');
        inputRef.current?.focus();
    };
    return (_jsxs("div", { ref: containerRef, className: "tag-select-field", children: [_jsxs("div", { className: "tag-select-field__header", children: [_jsx("label", { htmlFor: inputId, className: "tag-select-field__label", children: _jsx(RequiredLabel, { label: label, required: required, showRequiredText: showRequiredText || Boolean(error) }) }), helperText && _jsx("p", { className: "tag-select-field__helper", children: helperText })] }), _jsxs("div", { className: "tag-select-field__body", children: [_jsxs("div", { className: `tag-select-field__control${error ? ' tag-select-field__control--error' : ''}${dropdownOpen ? ' tag-select-field__control--open' : ''}`, onClick: () => inputRef.current?.focus(), children: [selectedValues.map((value) => (_jsxs("span", { className: "tag-select-field__chip", children: [_jsx("span", { className: "tag-select-field__chip-label", children: value }), _jsx("button", { type: "button", className: "tag-select-field__chip-remove", onMouseDown: (e) => e.stopPropagation(), onClick: (e) => {
                                            e.stopPropagation();
                                            handleRemove(value);
                                        }, "aria-label": `Remove ${value}`, children: "\u00D7" })] }, value))), _jsx("input", { ref: inputRef, id: inputId, name: name, type: "text", className: "tag-select-field__input", value: query, placeholder: selectedValues.length === 0 ? placeholder : '', onFocus: () => setDropdownOpen(true), onChange: (e) => {
                                    setQuery(e.target.value);
                                    setDropdownOpen(true);
                                } }), _jsxs("span", { className: "tag-select-field__multi-badge", "aria-hidden": "true", children: [_jsx("span", { className: "tag-select-field__multi-badge-head", children: "mul" }), _jsx("span", { className: "tag-select-field__multi-badge-tail", children: "ti" })] })] }), dropdownOpen && (_jsx("div", { className: "tag-select-field__picker", children: _jsx("ul", { className: "tag-select-field__option-list", role: "listbox", children: filtered.length === 0 ? (_jsx("li", { className: "tag-select-field__option-empty", children: query ? 'No results' : 'All options selected' })) : (filtered.map((opt) => (_jsx("li", { role: "option", "aria-selected": false, className: "tag-select-field__option", onMouseDown: (e) => e.preventDefault(), onClick: () => handleAdd(opt.value), children: opt.label }, opt.value)))) }) }))] })] }));
}
//# sourceMappingURL=TagSelectField.js.map